import { useState, useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

// @mui components
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Collapse,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
  useMediaQuery,
  Backdrop
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Dashboard,
  Business,
  People,
  Assignment,
  AccountBalance
} from "@mui/icons-material";

// Argon Dashboard components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Context
import { useArgonController, setMiniSidenav } from "context";

// Styles
import styles from "./MobileNavigation.module.css";

function MobileNavigation({ routes, brandName = "PRM Core", brand = null }) {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkMode } = controller;
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  // Auto-close mobile drawer on route change
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Handle drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Handle collapse toggle
  const handleExpandClick = (key) => {
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get icon component
  const getIcon = (iconName) => {
    const iconMap = {
      dashboard: Dashboard,
      business: Business,
      people: People,
      assignment: Assignment,
      account_balance: AccountBalance
    };
    
    const IconComponent = iconMap[iconName] || Dashboard;
    return <IconComponent />;
  };

  // Filter main navigation items for bottom nav
  const bottomNavItems = routes
    .filter(route => route.type === "route" && !route.hide)
    .slice(0, 5); // Limit to 5 items for bottom nav

  // Render navigation items
  const renderNavItems = (items, isNested = false) => {
    return items.map((item) => {
      if (item.type === "divider") {
        return <hr key={item.key} className={styles.divider} />;
      }

      if (item.type === "title") {
        return (
          <ArgonTypography
            key={item.key}
            variant="caption"
            fontWeight="bold"
            color="text"
            sx={{ p: 2, pt: isNested ? 1 : 2 }}
          >
            {item.title}
          </ArgonTypography>
        );
      }

      if (item.collapse) {
        return (
          <div key={item.key}>
            <ListItem 
              button 
              onClick={() => handleExpandClick(item.key)}
              sx={{ pl: isNested ? 4 : 2 }}
            >
              <ListItemIcon>
                {getIcon(item.icon)}
              </ListItemIcon>
              <ListItemText 
                primary={item.name}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              />
              {expandedItems[item.key] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse in={expandedItems[item.key]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {renderNavItems(item.collapse, true)}
              </List>
            </Collapse>
          </div>
        );
      }

      return (
        <ListItem
          key={item.key}
          component={NavLink}
          to={item.route}
          button
          sx={{
            pl: isNested ? 4 : 2,
            '&.active': {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white'
              }
            }
          }}
        >
          <ListItemIcon>
            {getIcon(item.icon)}
          </ListItemIcon>
          <ListItemText 
            primary={item.name}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          />
        </ListItem>
      );
    });
  };

  // Mobile drawer content
  const drawerContent = (
    <ArgonBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <ArgonBox 
        sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #eee'
        }}
      >
        <ArgonBox display="flex" alignItems="center">
          {brand && (
            <ArgonBox component="img" src={brand} alt="PRM Logo" width="48px" height="48px" />
          )}
        </ArgonBox>
        <IconButton onClick={handleDrawerToggle} size="small">
          <CloseIcon />
        </IconButton>
      </ArgonBox>

      {/* Navigation */}
      <ArgonBox sx={{ flex: 1, overflowY: 'auto' }}>
        <List>
          {renderNavItems(routes)}
        </List>
      </ArgonBox>
    </ArgonBox>
  );

  // Bottom navigation for mobile
  const BottomNav = () => {
    const currentPath = location.pathname;
    const activeIndex = bottomNavItems.findIndex(item => 
      currentPath.startsWith(item.route)
    );

    return (
      <BottomNavigation
        value={activeIndex >= 0 ? activeIndex : false}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: '1px solid #eee',
          backgroundColor: darkMode ? '#1a1a1a' : 'white'
        }}
      >
        {bottomNavItems.map((item, index) => (
          <BottomNavigationAction
            key={item.key}
            label={item.name}
            icon={getIcon(item.icon)}
            component={NavLink}
            to={item.route}
            sx={{
              minWidth: 'auto',
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            }}
          />
        ))}
      </BottomNavigation>
    );
  };

  if (isMobile) {
    return (
      <>
        {/* Mobile Top Bar */}
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            color: darkMode ? 'white' : 'black',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {brandName}
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Backdrop for drawer */}
        <Backdrop 
          open={mobileOpen} 
          onClick={handleDrawerToggle}
          sx={{ zIndex: theme.zIndex.drawer - 1 }}
        />

        {/* Bottom Navigation */}
        <BottomNav />
      </>
    );
  }

  // For tablet/desktop, return null as regular Sidenav will be used
  return null;
}

MobileNavigation.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  brandName: PropTypes.string,
  brand: PropTypes.string,
};


export default MobileNavigation;